import { Controller, useWatch } from "react-hook-form";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  Description,
  Error,
  FormItem,
  InputLabel,
  Required,
  CustomTooltip,
  AntdDatePickerInput,
  CONTROLLED_DATE_PICKER_THEME,
} from "./styles";
import { MdError, MdInfo } from "react-icons/md";
import { useIntl } from "react-intl";
import { useEffect, useState } from "react";

dayjs.extend(customParseFormat);

const DEFAULT_DATE_FORMAT = "DD/MM/YYYY";

// antd 4.x + dayjs equivalent of `ControlledDatePicker`, following the same Controller/field wiring
export default function ControlledDatePickerAntd({
  name,
  control,
  label,
  required,
  description,
  placeholder,
  errors,
  arrayName,
  onChange,
  tooltipText,
  disabled,
  format = DEFAULT_DATE_FORMAT,
  minDate,
  maxDate,
  disabledDate,
  theme,
  ...props
}) {
  const [error, setError] = useState(false);
  const intl = useIntl();

  const dateWatch = useWatch({ control, name });

  const hasDesc = description && (
    <Description>
      (
      {intl.formatMessage({
        id: description,
      })}
      )
    </Description>
  );

  const hasTooltip = tooltipText && (
    <CustomTooltip
      title={intl.formatMessage({ id: tooltipText })}
      placement="right"
      color="#003CB2"
    >
      <MdInfo size="16px" color="#dadada" />
    </CustomTooltip>
  );

  useEffect(() => {
    if (dateWatch) {
      return setError(false);
    }

    if (arrayName) {
      const [_, index, field] = name.split(".");
      const hasError = errors?.[arrayName]?.[index]?.[field];
      return setError(!!hasError);
    }

    if (errors?.[name]) {
      return setError(true);
    }

    return setError(false);
  }, [errors, dateWatch, name]);

  const labelIntl = intl.formatMessage({
    id: label,
  });

  const pickerTheme = { ...CONTROLLED_DATE_PICKER_THEME, ...theme };
  const isRequired = required && <Required>*</Required>;

  const handleDisabledDate = (current) => {
    if (disabledDate) return disabledDate(current);
    if (!current) return false;
    if (minDate && current.isBefore(dayjs(minDate), "day")) return true;
    if (maxDate && current.isAfter(dayjs(maxDate), "day")) return true;
    return false;
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        return (
          <FormItem error={error}>
            <InputLabel htmlFor={name} tooltip={tooltipText}>
              {labelIntl}
              {isRequired}
              {hasDesc}
              {hasTooltip}
            </InputLabel>
            <AntdDatePickerInput
              {...props}
              id={name}
              format={format}
              value={field.value ? dayjs(field.value) : null}
              onChange={(value) => {
                const parsedValue = value ? value.toDate() : null;
                field.onChange(parsedValue);
                if (onChange) onChange(parsedValue);
              }}
              placeholder={placeholder}
              disabled={disabled}
              disabledDate={
                disabledDate || minDate || maxDate
                  ? handleDisabledDate
                  : undefined
              }
              $theme={pickerTheme}
              $isErrored={error}
            />

            <ErrorMessage
              errors={errors}
              name={name}
              arrayName={arrayName}
              error={error}
            />
          </FormItem>
        );
      }}
    />
  );
}

function ErrorMessage({ errors, name, arrayName, error }) {
  const intl = useIntl();

  if (!error) return null;

  const getMessage = () => {
    if (!errors) return null;

    let message;

    if (arrayName) {
      const [_, index, field] = name.split(".");
      message = errors[arrayName]?.[index]?.[field]?.message;
    } else {
      message = errors[name]?.message;
    }

    if (!message) return null;

    switch (true) {
      case message.includes("end_date_error"): {
        return intl.formatMessage({ id: "end_date_error" });
      }
      default: {
        return intl.formatMessage({ id: "error.field_required" });
      }
    }
  };

  const errorMessage = getMessage();
  if (!errorMessage) return null;

  return (
    <Error>
      <MdError color="#DC2626" size={14} />
      {errorMessage}
    </Error>
  );
}

export function plusOneDay(date) {
  const newDate = new Date(date);
  newDate.setDate(date.getDate() + 1);

  return newDate;
}
